import type { MessageKey } from './en';

/**
 * SAM-683 — Spanish copy of the admin, for the Dominican business staff (front desk, business
 * admin). Neutral Latin American Spanish, "usted", one term per concept:
 *   booking → reserva · pass → pase · guest → huésped · business → negocio · service → servicio
 *   add-on → adicional · rate group → grupo de tarifa · cabana/unit → cabaña/unidad
 *   season → temporada · payment → pago · refund → reembolso · team → equipo
 * Same wording as the existing Spanish admin copy (utils/pricingErrors.ts) and, where the same
 * thing is said, the public pages (public/payLink/payLinkStrings.ts).
 */
export const es: Record<MessageKey, string> = {
  'common.loading': 'Cargando…',
  'common.cancel': 'Cancelar',
  'common.back': 'Atrás',
  'common.continue': 'Continuar',
  'common.next': 'Siguiente',
  'common.edit': 'Editar',
  'common.done': 'Listo',
  'common.required': 'Requerido',
  'common.notAvailable': 'N/D',
  'common.status.active': 'Activo',
  'common.status.inactive': 'Inactivo',

  'header.appName': 'Administración de negocios',
  'header.selectBusiness': 'Seleccionar negocio',
  'header.user': 'Usuario',
  'header.userInitial': 'U',
  'header.logOut': 'Cerrar sesión',

  'business.tab.activity': 'Actividad',
  'business.tab.services': 'Servicios',
  'business.tab.team': 'Equipo',
  'business.tab.settings': 'Configuración',
  'business.section.profile': 'Perfil',
  'business.section.schedule': 'Horario',
  'business.section.closures': 'Cierres y días especiales',
  'business.section.advanced': 'Configuración avanzada',
  'business.tabsLabel': 'Secciones del negocio',
  'business.sectionsLabel': 'Secciones de configuración',
  'business.loading': 'Cargando negocio…',
  'business.notFound': 'No se encontró el negocio.',
  'business.backToList': 'Volver a negocios',
  'business.leave.title': '¿Salir sin guardar?',
  'business.leave.body': 'Tienes cambios sin guardar en este negocio. Se perderán.',
  'business.leave.bodyHours': 'Perderás los cambios en el horario.',
  'business.leave.stay': 'Seguir editando',
  'business.leave.leave': 'Descartar cambios',
  'business.new': 'Nuevo negocio',
  'business.fallbackName': 'Negocio',

  'team.searchPlaceholder': 'Buscar miembros del equipo…',
  'team.addMember': 'Agregar miembro',
  'team.loading': 'Cargando equipo…',
  'team.error': 'No se pudo cargar el equipo.',
  'team.retry': 'Reintentar',
  'team.emptySearch': 'Ningún miembro del equipo coincide con su búsqueda.',
  'team.empty': 'Este negocio aún no tiene miembros en el equipo.',
  'team.frontDeskNote': 'Los miembros que agregas los revisa Seguridad antes de darles acceso.',
  'team.col.member': 'Miembro',
  'team.col.role': 'Rol',
  'team.col.status': 'Estado',
  'team.chip.admin': 'administrador',
  'team.chip.invitation': 'recepción',
  'team.chip.operator': 'operador',
  'team.chip.owner': 'propietario',
  'team.status.invited': 'Invitado',
  'team.role.admin': 'Administrador',
  'team.role.frontDesk': 'Recepción',
  'team.noRole': 'Sin rol en el negocio',
  'team.backToTeam': 'Volver al equipo',
  'team.editDetails': 'Editar datos',
  'team.keepActive': 'Mantener activo',
  'team.deactivating': 'Desactivando…',
  'team.confirmDeactivate': 'Confirmar desactivación',
  'team.deactivate': 'Desactivar',
  'team.activating': 'Activando…',
  'team.activate': 'Activar',
  'team.identityDocument': 'Documento de identidad',
  'team.idOnFile': 'Registrado',
  'team.idMissing': 'Falta',
  'team.idType.documentId': 'Cédula',
  'team.idType.passport': 'Pasaporte',
  'team.idType.driverLicense': 'Licencia de conducir',
  'team.access': 'Acceso',
  'team.field.firstName': 'Nombre',
  'team.field.lastName': 'Apellido',
  'team.field.dateOfBirth': 'Fecha de nacimiento',
  'team.field.idType': 'Tipo de documento',
  'team.field.idNumber': 'Número de documento',
  'team.field.nationality': 'Nacionalidad',
  'team.field.contactNumber': 'Teléfono de contacto',
  'team.field.signIn': 'Inicio de sesión',
  'team.field.role': 'Rol',
  'team.field.business': 'Negocio',
  'team.field.status': 'Estado',
  'team.field.added': 'Agregado',
  'team.field.lastChange': 'Último cambio',
  'team.identity': 'Identidad',
  'team.document': 'Documento',
  'team.contactAccess': 'Contacto y acceso',
  'team.field.phone': 'Teléfono',
  'team.field.email': 'Correo',
  'team.field.verified': 'Verificado',

  'reset.button': 'Restablecer contraseña',
  'reset.title': '¿Restablecer la contraseña?',
  'reset.send': 'Enviar código',
  'reset.sending': 'Enviando…',
  'reset.sent': 'Código enviado a {who}.',
  'reset.bodyEmail': 'Enviaremos un código de verificación por correo a',
  'reset.bodyBefore': 'Su contraseña actual deja de funcionar de inmediato. Para volver a entrar debe elegir',
  'reset.forgotPassword': 'Olvidé mi contraseña',
  'reset.bodyAfter': ', ingresar el código y crear una contraseña nueva.',
  'reset.reason.ownAccount': 'Esta es su propia cuenta. Use "Olvidé mi contraseña" en la pantalla de inicio de sesión.',
  'reset.reason.noPermission': 'Necesita permiso de gestión de usuarios para restablecer contraseñas.',
  'reset.reason.otherBusiness': 'Solo puede restablecer contraseñas de usuarios de su propio negocio.',
  'reset.reason.xhinola': 'Las cuentas de Xhinola no se pueden restablecer desde aquí.',
  'reset.reason.adminOnly': 'Solo un administrador general puede restablecer a otro administrador.',
  'reset.reason.inactive': 'El usuario está inactivo. Actívelo primero.',
  'reset.reason.neverSignedIn': 'El usuario aún no ha iniciado sesión, así que no tiene contraseña que restablecer.',
  'reset.error.network': 'No se pudo conectar con el servidor. Revise la conexión e intente de nuevo.',
  'reset.error.notDeployed': 'El restablecimiento de contraseña aún no está disponible en este entorno.',
  'reset.error.forbidden': 'No tiene permiso para restablecer la contraseña de este usuario.',
  'reset.error.notFound': 'Este usuario ya no existe.',
  'reset.error.noVerifiedContact': 'El usuario no tiene correo ni teléfono verificado, así que Cognito no puede enviar el código.',
  'reset.error.tooMany': 'Demasiados restablecimientos para este usuario. Intente más tarde.',
  'reset.error.rejected': 'La solicitud de restablecimiento no fue aceptada.',
  'reset.error.generic': 'No se pudo restablecer la contraseña. Intente de nuevo.',

  'payments.title': 'Transacciones',
  'payments.filters': 'Filtros',
  'payments.clear': 'Limpiar',
  'payments.searchPlaceholder': 'Buscar por referencia, transacción, tarjeta, monto...',
  'payments.col.reference': 'REFERENCIA',
  'payments.col.txnRef': 'REF. TRANSACCIÓN',
  'payments.col.date': 'FECHA',
  'payments.col.amount': 'MONTO',
  'payments.col.card': 'TARJETA',
  'payments.col.status': 'ESTADO',
  'payments.col.refunded': 'REEMBOLSADO',
  'payments.col.gateway': 'PASARELA',
  'payments.col.business': 'NEGOCIO',
  'payments.remaining': 'Restante {amount}',
  'payments.status.success': 'Aprobado',
  'payments.status.failed': 'Fallido',
  'payments.status.voided': 'Anulado',
  'payments.status.partially_refunded': 'Reembolsado en parte',
  'payments.status.refunded': 'Reembolsado',
  'payments.filter.status': 'Estado',
  'payments.filter.all': 'Todos',
  'payments.filter.dateRange': 'Rango de fechas',
  'payments.filter.selectRange': 'Seleccionar rango...',
  'payments.filter.minAmount': 'Monto mínimo',
  'payments.filter.maxAmount': 'Monto máximo',

  'booking.businessIdRequired': 'Se requiere el negocio',
  'booking.service.pricesError': 'No se pudieron cargar los precios de este servicio. Recargue la página e intente de nuevo.',
  'booking.service.pricesLoading': 'Cargando precios del servicio…',
  'booking.service.loadingShort': 'Cargando servicios...',
  'booking.service.loading': 'Cargando servicios…',
  'booking.service.loadError': 'Error al cargar los servicios',
  'booking.service.noneBookable': 'No hay servicios disponibles para reservar',
  'booking.service.select': 'Seleccione un servicio',
  'booking.service.noneSynced': 'Ningún servicio de este negocio está sincronizado con el motor de reservas todavía.',
  'booking.access.event': 'Evento',
  'booking.event.loadingShort': 'Cargando eventos...',
  'booking.event.loading': 'Cargando eventos…',
  'booking.event.loadError': 'Error al cargar los eventos',
  'booking.event.none': 'No hay eventos abiertos',
  'booking.event.select': 'Seleccione un evento',
  'booking.access.pickEvent': 'Seleccione primero un evento.',
  'booking.product.loading': 'Cargando productos...',
  'booking.product.loadError': 'Error al cargar los productos',
  'booking.product.none': 'No hay productos disponibles',
  'booking.product.select': 'Seleccione un producto',
  'booking.access.eyebrow': 'Paso 1 de 4',
  'booking.access.title': 'Acceso',
  'booking.access.description': 'Para quién es, a dónde va y por cuánto tiempo.',
  'booking.access.destination': 'Destino',
  'booking.access.accessType': 'Tipo de acceso',
  'booking.access.product': 'Producto',
  'booking.access.service': 'Servicio',
  'booking.access.pickDestinationProducts': 'Seleccione primero un destino para ver los productos disponibles.',
  'booking.access.pickDestinationServices': 'Seleccione primero un destino para ver los servicios disponibles.',
  'booking.access.howLong': '¿Por cuánto tiempo?',
  'booking.access.pickDestination': 'Seleccione primero un destino.',
  'booking.access.singleDayReason': 'Las reservas de servicios son de un solo día; elija "Un día" arriba.',
  'booking.access.timeAndQuantities': 'Hora y cantidades',
  'booking.destination.loadingBusinesses': 'Cargando negocios...',
  'booking.destination.errorBusinesses': 'Error al cargar los negocios',
  'booking.destination.selectBusiness': 'Seleccione un negocio',
  'booking.destination.loadingGroups': 'Cargando grupos de propiedades...',
  'booking.destination.errorGroups': 'Error al cargar los grupos de propiedades',
  'booking.destination.selectGroup': 'Seleccione un grupo de propiedades',
  'booking.destination.loadingBuildings': 'Cargando edificios...',
  'booking.destination.allBuildings': 'Todos los edificios (opcional)',
  'booking.destination.pickGroupFirst': 'Seleccione primero un grupo de propiedades',
  'booking.destination.loadingEstates': 'Cargando propiedades...',
  'booking.destination.errorEstates': 'Error al cargar las propiedades',
  'booking.destination.selectEstate': 'Seleccione una propiedad',
  'booking.destination.noEstates': 'Este grupo no tiene propiedades',
  'booking.kind.residential': 'Residencial',
  'booking.kind.business': 'Negocio',
  'booking.kind.event': 'Evento',
  'booking.accessType.loadingBusiness': 'Cargando tipos de acceso del negocio...',
  'booking.accessType.loadingResidential': 'Cargando tipos de acceso residencial...',
  'booking.accessType.errorBusiness': 'Error al cargar los tipos de acceso del negocio',
  'booking.accessType.errorResidential': 'Error al cargar los tipos de acceso residencial',
  'booking.accessType.select': 'Seleccione un tipo de pase',
  'booking.readiness.oneDayNotReady': 'Elija un día y confirme Adultos/Niños en "¿Por cuánto tiempo?" para ver la disponibilidad de este servicio.',
  'booking.readiness.multiDay': 'Las reservas de servicios son de un solo día; elija "Un día" arriba para ver la disponibilidad de este servicio.',
  'booking.readiness.maxDays': '(este servicio permite hasta {n} día(s) por reserva y por ahora solo se puede reservar una fecha a la vez).',
  'booking.readiness.minDays': '(este servicio requiere al menos {n} día(s) y por ahora solo se puede reservar una fecha a la vez).',

  'booking.howLong.oneDay': 'Un día',
  'booking.howLong.multipleDays': 'Varios días',
  'booking.howLong.singleDayOnly': 'Las reservas de servicios son de un solo día.',
  'booking.howLong.today': 'Hoy',
  'booking.howLong.selectRange': 'Seleccione el rango de fechas',
  'booking.howLong.rangePlaceholder': 'Haga clic para elegir fecha de inicio y fin',
  'booking.howLong.selectedRange': 'Rango de fechas elegido',
  'booking.howLong.howMany': '¿Cuántos?',
  'booking.howLong.adults': 'Adultos',
  'booking.howLong.adultsHint': 'Mayores de 13 años',
  'booking.howLong.children': 'Niños',
  'booking.howLong.childrenHint': 'De 2 a 12 años',
  'booking.howLong.total': 'Total',
  'booking.howLong.guestsTotal': '{n} huéspedes',
  'booking.phone.title': 'Nuevo pase',
  'booking.phone.close': 'Cerrar',
  'booking.phone.stepOf': 'Paso {n} de {total}',
  'booking.phone.whatDate': '¿Qué fecha?',
  'booking.phone.tomorrow': 'Mañana',
  'booking.phone.moreDates': 'Más fechas',
  'booking.phone.calendar': 'Calendario',
  'booking.phone.pickDay': 'Elige un día',
  'booking.phone.from': 'Desde',
  'booking.phone.to': 'Hasta',
  'booking.phone.howMany': '¿Cuántos invitados?',
  'booking.phone.adultsHint': 'De 13 años en adelante',
  'booking.phone.upTo': 'Hasta {n} personas.',
  'booking.phone.reading': 'Leyendo la identificación…',
  'booking.phone.addIdPhoto': 'Agregar foto de ID',
  'booking.phone.or': 'O',
  'booking.phone.fromContacts': 'Agregar de contactos',
  'booking.phone.fromContactsHint': 'Invitados que vinieron antes',
  'booking.phone.shareRegistration': 'Compartir enlace de registro',
  'booking.phone.shareRegistrationHint': 'Los invitados llenan sus propios datos',
  'booking.phone.scannedDocument': 'DOCUMENTO ESCANEADO',
  'booking.phone.rescan': 'Volver a escanear',
  'booking.phone.optional': 'Opcional',
  'booking.phone.scanNote': 'Nombre y apellido son obligatorios. El número de ID se lee de la foto si lo dejas vacío.',
  'booking.phone.firstName': 'Nombre',
  'booking.phone.lastName': 'Apellido',
  'booking.phone.idNumber': 'Número de ID',
  'booking.phone.nationality': 'Nacionalidad',
  'booking.phone.dob': 'Fecha de nacimiento',
  'booking.phone.reachTitle': '¿Cómo los contactamos?',
  'booking.phone.reachHint': 'El invitado principal recibe el pase por SMS.',
  'booking.phone.phoneNumber': 'Número de teléfono',
  'booking.phone.brandNotAccepted': 'No aceptamos {brand}. Usa Visa, Mastercard o American Express.',
  'booking.phone.thatCard': 'esa tarjeta',
  'booking.phone.payWithCard': 'Pagar con tarjeta',
  'booking.phone.cardNote': 'La tarjeta se envía a SAM, que hace el cobro. No se guarda en este dispositivo.',
  'booking.phone.mainGuest': 'Invitado principal',
  'booking.phone.companionN': 'Invitado {n}',
  'booking.phone.bookingDetails': 'Detalles de la reserva',
  'booking.phone.date': 'Fecha',
  'booking.phone.guests': 'Invitados',
  'booking.phone.price': 'Precio',
  'booking.phone.sentBySms': 'Pase enviado por SMS al invitado principal',
  'booking.phone.sharePass': 'Compartir pase',
  'booking.phone.done': 'Listo',
  'booking.phone.createPass': 'Crear pase',
  'booking.phone.passCreated': 'Pase creado',
  'booking.phone.persons': 'Personas',
  'booking.phone.dates': 'Fechas',
  'booking.phone.business': 'Negocio',
  'booking.phone.adultsN': '{n} adultos',
  'booking.phone.adultN': '1 adulto',
  'booking.phone.childN': '1 niño',
  'booking.phone.childrenN': '{n} niños',
  'booking.phone.allDay': 'Todo el día',
  'booking.phone.enterManually': 'Escribir los datos',
  'booking.phone.phoneIncomplete': 'Escribe el número completo',
  'booking.phone.registrationHint': 'Le enviamos al invitado un enlace para registrarse cuando se cree el pase.',
  'booking.phone.fewer': 'Menos {what}',
  'booking.phone.more': 'Más {what}',
  'booking.howLong.ofMax': ' / máx. {n}',
  'booking.howLong.maxAdultsOne': 'Este tipo de pase permite un máximo de {n} adulto.',
  'booking.howLong.maxAdultsMany': 'Este tipo de pase permite un máximo de {n} adultos.',
  'booking.howLong.maxChildrenOne': 'Este tipo de pase permite un máximo de {n} niño.',
  'booking.howLong.maxChildrenMany': 'Este tipo de pase permite un máximo de {n} niños.',
  'booking.howLong.maxPerDay': 'Este tipo de pase permite un máximo de {n} huéspedes por día.',

  'booking.slots.pickDate': 'Elija una fecha arriba ("¿Por cuánto tiempo?") para ver la disponibilidad de este servicio.',
  'booking.slots.loading': 'Cargando disponibilidad…',
  'booking.slots.loadError': 'No se pudo cargar la disponibilidad de este servicio.',
  'booking.slots.localFallback': 'La disponibilidad puede estar incompleta: este servicio no está leyendo en vivo del motor de reservas.',
  'booking.slots.noneOnDate': 'Este servicio no tiene disponibilidad el {date}. Elija otra fecha arriba.',
  'booking.slots.fullDay': 'Reserva de día completo el {date}{hours}; no hay hora que elegir.',
  'booking.slots.left': 'quedan {n}',

  'booking.price.flatEvent': 'precio fijo por evento',
  'booking.price.perExtraGuest': '+{price} por huésped adicional',
  'booking.price.perUnit': '{price} / unidad',
  'booking.rateGroup.label': 'Grupo de tarifa',
  'booking.rateGroup.for': 'Grupo de tarifa para {option}',
  'booking.rateGroup.choose': 'Elegir…',
  'booking.unit.title': 'Cabaña / unidad',
  'booking.unit.whichOne': '¿Cuál se está reservando?',
  'booking.unit.ariaLabel': 'Cabaña o unidad',
  'booking.unit.select': 'Seleccione una cabaña…',
  'booking.unit.describe': '{price} · {included} incl.',
  'booking.unit.max': 'máx. {max}',
  'booking.unit.priceNotSet': 'sin precio',
  'booking.unit.fallbackName': 'Unidad',
  'booking.unit.includesOne': '{price} incluye {n} huésped',
  'booking.unit.includesMany': '{price} incluye {n} huéspedes',
  'booking.unit.upTo': ', hasta {max}',
  'booking.unit.extraOne': '{n} huésped adicional paga la tarifa por huésped',
  'booking.unit.extraMany': '{n} huéspedes adicionales pagan la tarifa por huésped',
  'booking.unit.allIncluded': 'todos dentro de los huéspedes incluidos',
  'booking.guests.one': '{n} huésped',
  'booking.guests.many': '{n} huéspedes',
  'booking.event.size': 'Tamaño del evento',
  'booking.event.attendeesToSize': '{n} asistentes → tamaño',
  'booking.event.flatOnce': 'fijo, se cobra una sola vez por todo el evento.',
  'booking.event.noPrice': 'Aún no hay precio para esta cantidad de asistentes.',
  'booking.event.setCount': 'Indique la cantidad de asistentes arriba para ver el tamaño.',
  'booking.season.pricesApply': 'Aplican precios de {season}',

  'booking.qty.cannotDerive': 'Las opciones de este servicio no se pueden calcular automáticamente desde Adultos/Niños; indique las cantidades a mano.',
  'booking.qty.fromAdultsChildren': 'La cantidad de huéspedes viene de Adultos/Niños arriba ({n} en total).',
  'booking.qty.includedInUnit': '{n} incluidos en el precio de la unidad',
  'booking.qty.includedFree': '{n} incluidos gratis',
  'booking.qty.extra': '{n} adicionales',
  'booking.qty.billable': '{n} con cargo',
  'booking.qty.decrease': 'Disminuir {item}',
  'booking.qty.increase': 'Aumentar {item}',
  'booking.qty.minimum': '{option}: el mínimo es {n}.',
  'booking.qty.maximum': '{option}: el máximo es {n}.',
  'booking.qty.atLeastOne': 'Seleccione al menos un huésped.',
  'booking.qty.noOptions': 'Este servicio no tiene opciones de reserva configuradas.',
  'booking.qty.tierGroupManual': 'El grupo de niveles de huéspedes de este servicio ("{group}") tiene {n} niveles; indique las cantidades a mano.',
  'booking.addOns': 'Adicionales',
  'booking.addOnRequired': 'obligatorio',
  'booking.totalConfirmedByServer': 'El servidor confirma el precio total al enviar la reserva.',

  'booking.summary.title': 'Resumen de la reserva',
  'booking.summary.destination': 'Destino',
  'booking.summary.accessType': 'Tipo de acceso',
  'booking.summary.product': 'Producto',
  'booking.summary.service': 'Servicio',
  'booking.summary.date': 'Fecha',
  'booking.summary.time': 'Hora',
  'booking.summary.fullDay': 'Día completo',
  'booking.summary.guests': 'Huéspedes',
  'booking.summary.total': '{n} en total',
  'booking.summary.adultOne': '{n} adulto',
  'booking.summary.adultMany': '{n} adultos',
  'booking.summary.childOne': '{n} niño',
  'booking.summary.childMany': '{n} niños',
  'booking.summary.extraGuests': 'Huéspedes adicionales',
  'booking.summary.event': 'Evento',
  'booking.summary.perUnitPrices': 'Precios por unidad',
  'booking.summary.guestsIncluded': '{n} huéspedes incluidos',
  'booking.summary.flat': '{price} fijo',
  'booking.summary.seasonPricing': 'Precio de temporada',
  'booking.summary.estimatedTotal': 'Total estimado',
  'booking.summary.nothingSelected': 'Aún no hay nada seleccionado.',

  'booking.payment.title': 'Pago',
  'booking.payment.description': 'Los datos de la tarjeta se procesan con CyberSource y 3DS; aquí no se guarda nada sin cifrar.',
  'booking.payment.when': '{date} a las {time}',
  'booking.payment.cardOptional': 'Ingrese una tarjeta solo si esta reserva es pagada; el servidor confirma el monto. Déjela en blanco para una reserva gratuita.',

  'card.number': 'Número de tarjeta',
  'card.holder': 'Nombre en la tarjeta',
  'card.expiry': 'Vence (MM/AA)',
  'card.cvv': 'CVV',
  'card.required': 'Requerido',
  'card.invalidNumber': 'El número debe tener 16 dígitos',
  'card.invalidExpiry': 'Use MM/AA',
  'card.expired': 'La tarjeta está vencida',
  'card.invalidCvv': 'El CVV debe tener 3 o 4 dígitos',
  'card.invalidName': 'Ingrese el nombre que aparece en la tarjeta',

  'invitation.action.shareLink': 'Compartir enlace',
  'invitation.action.print': 'Imprimir',
  'invitation.action.confirm': 'Confirmar',
  'invitation.action.checkIn': 'Registrar entrada',
  'invitation.action.checkOut': 'Registrar salida',
  'invitation.action.cancel': 'Cancelar',
  'invitation.status.pending': 'Pendiente',
  'invitation.status.confirmed': 'Confirmada',
  'invitation.status.verified': 'Verificada',
  'invitation.status.checkedIn': 'Entrada registrada',
  'invitation.status.checkedOut': 'Salida registrada',
  'invitation.status.cancelled': 'Cancelada',
  'invitation.status.expired': 'Vencida',
  'invitation.status.processing': 'En proceso',
  'invitation.status.unknown': 'Desconocido',
  'invitation.guestType.guest': 'Huésped',
  'invitation.guestType.business': 'Negocio',
  'invitation.guestType.vip': 'VIP',
  'invitation.guestType.event': 'Evento',
  'invitation.guestType.other': 'Tipo {id}',
  'passLink.title': 'Link de pago',
  'passLink.expires': 'vence a las {time}',
  'passLink.copy': 'Copiar link de pago',
  'passLink.copyFailed': 'No se pudo copiar el link',
  'passLink.smsFailed': 'El link no se envió por SMS. Cópialo y envíaselo al huésped.',
  'passConfirm.title': 'Confirmar',
  'passConfirm.destination': 'A dónde va el huésped',
  'passConfirm.mainGuest': 'Huésped principal',
  'passConfirm.dates': 'Fechas',
  'passConfirm.guests': 'Huéspedes',
  'passConfirm.idDocument': 'Documento de identidad',
  'passConfirm.idScanned': 'Escaneado',
  'passConfirm.idEntered': 'Capturado',
  'passConfirm.idByLink': 'El huésped lo completa por enlace',
  'passConfirm.payment': 'Pago',
  'passConfirm.card': 'Tarjeta ····{last4}',
  'passConfirm.link': 'Link de pago',
  'passConfirm.continue': 'Continuar a enviar',
  'invitation.linkCopied': '¡Enlace copiado!',
  'invitation.linkCopyFailed': 'No se pudo copiar el enlace: no se encontró el código de acceso',
  'invitation.noData': 'No hay datos de la invitación',
  'invitation.moreActions': 'Más acciones',
  'invitation.scanQr': 'Escanee el código QR',
  'invitation.field.type': 'Tipo de pase',
  'invitation.field.guestType': 'Tipo de huésped',
  'invitation.field.whereTo': '¿A dónde?',
  'invitation.field.when': '¿Cuándo?',
  'invitation.field.guest': 'Huésped',
  'invitation.field.mainGuest': 'Huésped principal',
  'invitation.field.phone': 'Teléfono',
  'invitation.field.email': 'Correo',
  'invitation.field.gate': 'Puerta #',
  // SAM-708: invitations drawer
  'invitation.detail.hint.pending': 'Esperando aprobación',
  'invitation.detail.hint.confirmed': 'Lista para la llegada',
  'invitation.detail.hint.verified': 'Verificada, lista para la llegada',
  'invitation.detail.hint.checkedIn': 'El invitado está dentro',
  'invitation.detail.hint.checkedOut': 'El invitado ya salió',
  'invitation.detail.hint.cancelled': 'Este pase fue cancelado',
  'invitation.detail.awaitingPayment': 'Esperando el pago',
  'invitation.detail.awaitingPaymentExpires': 'Esperando el pago · vence {when}',
  'invitation.detail.hint.expired': 'Este pase expiró',
  'invitation.detail.do.confirm': 'Confirmar',
  'invitation.detail.do.checkIn': 'Registrar entrada',
  'invitation.detail.do.checkOut': 'Registrar salida',
  'invitation.detail.cancelInvitation': 'Cancelar pase',
  'invitation.detail.cancelShort': 'Cancelar',
  'invitation.detail.residential': 'Residencial',
  'invitation.detail.business': 'Negocio',
  'invitation.detail.guestFallback': 'Invitado',
  'invitation.detail.visit': 'Visita',
  'invitation.detail.whereTo': 'Destino',
  'invitation.detail.when': 'Cuándo',
  'invitation.detail.party': 'Grupo',
  'invitation.detail.gates': 'Puertas',
  'invitation.detail.allGates': 'Todas las puertas',
  'invitation.detail.oneDay': 'Un día',
  'invitation.detail.days': '{count} días',
  'invitation.detail.noDate': 'Fecha no indicada',
  'invitation.detail.adults': '{count} adultos',
  'invitation.detail.adult': '1 adulto',
  'invitation.detail.children': '{count} niños',
  'invitation.detail.child': '1 niño',
  'invitation.detail.pass': 'Pase de acceso',
  'invitation.detail.passHint': 'El invitado muestra este QR en la puerta.',
  'invitation.detail.share': 'Compartir',
  'invitation.detail.sms': 'SMS',
  'invitation.detail.copyLink': 'Copiar enlace',
  'invitation.detail.printPass': 'Imprimir pase',
  'invitation.detail.linkCopied': 'Enlace copiado',
  'invitation.detail.noLink': 'Este pase aún no tiene enlace',
  'invitation.detail.whatsappText': 'Hola {name}, este es tu pase de acceso: {link}',
  'invitation.detail.guests': 'Invitados ({count})',
  'invitation.detail.mainGuest': 'Invitado principal',
  'invitation.detail.companion': 'Acompañante {n}',
  'invitation.detail.idOnFile': 'ID registrado',
  'invitation.detail.noId': 'Sin ID',
  'invitation.detail.moreDetails': 'Más detalles',
  'invitation.detail.created': 'Creada',
  'invitation.detail.channel': 'Canal',
  'invitation.detail.passCode': 'Código del pase',
  'invitation.detail.payment': 'Pago',
  'invitation.detail.payment.paid': 'Pagado',
  'invitation.detail.payment.voided': 'Anulado',
  'invitation.detail.payment.refunded': 'Reembolsado',
  'invitation.detail.payment.partiallyRefunded': 'Reembolso parcial',
  'invitation.detail.payment.failed': 'Fallido',
  'invitation.detail.payment.pending': 'Pendiente',
  'invitation.gateAll': 'todas',
  'invitation.date.notSpecified': 'Fecha no indicada',
  'invitation.date.invalid': 'Fecha inválida',
  'invitation.date.notAvailable': 'Fecha no disponible',
  'userEstate.title': 'Unidades',
  'userEstate.summary': '{active} activas · {pending} pendientes · {declined} rechazadas',
  'userEstate.add': 'Agregar unidad',
  'userEstate.empty': 'Este usuario aún no tiene unidades.',
  'userEstate.loadError': 'No se pudieron cargar las unidades.',
  'userEstate.section.pending': 'Pendientes de aprobación',
  'userEstate.section.active': 'Activas',
  'userEstate.section.declined': 'Rechazadas',
  'userEstate.col.unit': 'Unidad',
  'userEstate.col.role': 'Rol',
  'userEstate.col.requested': 'Solicitada',
  'userEstate.col.expires': 'Vence',
  'userEstate.col.actions': 'Acciones',
  'userEstate.adminOnly': 'Solo admin',
  'userEstate.noGroup': 'Sin grupo',
  'userEstate.noRole': 'Sin rol',
  'userEstate.noExpiration': 'Sin vencimiento',
  'userEstate.expiresIn': 'en {n} días',
  'userEstate.expiresIn1': 'en 1 día',
  'userEstate.expiresToday': 'hoy',
  'userEstate.expired': 'vencida',
  'userEstate.approve': 'Aprobar',
  'userEstate.decline': 'Rechazar',
  'userEstate.revoke': 'Revocar',
  'userEstate.reactivate': 'Reactivar',
  'userEstate.revokeTitle': '¿Revocar el acceso?',
  'userEstate.revokeBody': '{unit} dejará de estar activa para este usuario. Se puede reactivar después.',
  'userEstate.back': 'Volver',
  'userEstate.roleLabel': 'Rol en {unit}',
  'userEstate.savingRole': 'Guardando…',
  'userEstate.expiryLabel': 'Vencimiento de {unit}',
  'userEstate.prevMonth': 'Mes anterior',
  'userEstate.nextMonth': 'Mes siguiente',
  'userEstate.expiryUpdated': 'Vencimiento actualizado',
  'userEstate.expiryFailed': 'No se pudo actualizar el vencimiento',
  'userEstate.statusUnavailable': 'Este estado no existe para el tenant.',
  'invitation.confirm.back': 'Volver',
  'invitation.confirm.confirmTitle': 'Confirmar pase',
  'invitation.confirm.confirmButton': 'Confirmar',
  'invitation.confirm.confirmBody': '¿Seguro que quieres confirmar este pase?',
  'invitation.confirm.checkInBody': '¿Seguro que quieres registrar la entrada de este invitado?',
  'invitation.confirm.checkOutBody': '¿Seguro que quieres registrar la salida de este invitado?',
  'invitation.confirm.cancelBody': '¿Seguro que quieres cancelar este pase? Si tiene un pago, el pago se anulará. Esta acción no se puede deshacer.',
  'invitation.confirm.guest': 'Invitado:',
  'invitation.confirm.destination': 'Destino:',
  'user.businessRequired': "Asigna un negocio a este usuario antes de agregar un rol de negocio.",
  'user.businessRequiredHint': "Defínelo en la sección Business de abajo, guarda y luego agrega el rol.",
  'passError.pass_expired': "Este pase ya terminó, por lo que no se puede modificar ni cancelar.",
  'passError.invalid_dates': "Las fechas no son válidas: un pase no puede terminar en el pasado ni antes de empezar.",
  'passError.guest_idnum_locked': "El documento del invitado principal no se puede cambiar. Para otra persona, crea otro pase.",
  'passError.use_invitation_endpoint': "Este cambio se hace en el pase mismo, no desde esta pantalla. Recarga la página e inténtalo de nuevo.",
  'passPayment.void_window_closed': "Pasó la hora de corte del banco para anular este pago, así que el pase no se canceló y el pago no cambió.",
  'passPayment.CANNOT_VOID_CHECKED_IN': "Este pase ya tiene check-in, así que su pago no se puede anular y el pase no se puede cancelar.",
  'passPayment.pass_has_payment': "Este pase tiene un pago que hay que anular primero. No se canceló nada; recarga e inténtalo de nuevo.",
  'passPayment.payment_in_progress': "El pago de este pase todavía se está procesando. Inténtalo de nuevo en unos minutos.",
  'passPayment.PAYMENT_LEDGER_UNAVAILABLE': "No se puede revisar el pago de este pase ahora, así que no se canceló nada. Inténtalo de nuevo.",
  'passPayment.forbidden': "El servidor rechazó cancelar este pase. Si tiene un pago, cancelarlo lo anula, y eso requiere permiso para anular pagos.",
  'appUser.deleted': "Cuenta borrada: ninguna cuenta de Cognito tiene el id de este usuario ({lookup}). No hay nada que administrar aquí.",
  'appUser.otherRow': "La cuenta con este correo pertenece al usuario {id}, no a este registro.",
  'appUser.openUser': "Abrir usuario {id}",
  'appUser.unverified': "No se puede confirmar que esta cuenta de Cognito sea de este registro (se encontró por correo y no trae el id de usuario). Solo lectura: sin acciones sobre la cuenta.",

  // ── Teléfono: menú, Pases y Actividad a 375 px (SAM-944) ──────────────────
  'header.openMenu': 'Abrir menú',
  'menu.logOut': 'Cerrar sesión',
  'menu.roleAdmin': 'Administrador',
  'menu.roleXhinola': 'Xhinola',
  'menu.roleFrontDesk': 'Recepción',
  'menu.roleEstateGroupAdmin': 'Admin de grupo de estates',
  'menu.roleEstateGroupFrontDesk': 'Recepción de grupo de estates',
  'phone.newPass': 'Nuevo pase',
  'phonePasses.title': 'Pases',
  'phonePasses.search': 'Buscar por huésped, código de reserva o PIN',
  'phonePasses.filterLabel': 'Mostrar pases',
  'phonePasses.filter.upcoming': 'Próximos',
  'phonePasses.filter.today': 'Hoy',
  'phonePasses.filter.past': 'Pasados',
  'phonePasses.filter.statusGroup': 'Estado',
  'phonePasses.empty': 'No hay pases para mostrar.',
  'phonePasses.allDay': 'Todo el día',
  'phonePasses.todayWhen': 'Hoy · {when}',
  'phonePasses.business': 'Negocio',
  'phonePasses.guest': 'Huésped',
  'phonePasses.bookingCode': 'Reserva {code}',
  'phonePasses.byHost': 'de {host}',
  'activityPhone.title': 'Actividad',
  'activityPhone.viewLabel': 'Mostrar',
  'activityPhone.view.bookings': 'Reservas',
  'activityPhone.view.passes': 'Pases',
  'activityPhone.view.transactions': 'Transacciones',
  'activityPhone.search.bookings': 'Buscar huésped o servicio',
  'activityPhone.search.passes': 'Buscar huésped o destino',
  'activityPhone.search.transactions': 'Buscar pagador, tarjeta o referencia',
  'activityPhone.filtersLabel': 'Filtros',
  'activityPhone.chip.today': 'Hoy',
  'activityPhone.chip.week': 'Esta semana',
  'activityPhone.chip.month': 'Este mes',
  'activityPhone.chip.custom': 'Fechas',
  'activityPhone.chip.checkedIn': 'Con ingreso',
  'activityPhone.chip.confirmed': 'Confirmadas',
  'activityPhone.chip.cancelled': 'Canceladas',
  'activityPhone.chip.paid': 'Pagadas',
  'activityPhone.chip.refunded': 'Reembolsadas',
  'activityPhone.from': 'Desde',
  'activityPhone.to': 'Hasta',
  'activityPhone.pickDay': 'Elija un día',
  'activityPhone.empty.bookings': 'No hay reservas con este filtro.',
  'activityPhone.empty.passes': 'No hay pases con este filtro.',
  'activityPhone.empty.transactions': 'No hay transacciones con este filtro.',
  'activityPhone.failed': 'No se pudo cargar toda la actividad.',
  'activityPhone.retry': 'Reintentar',
  'activityPhone.guestsOne': '{n} huésped',
  'activityPhone.guestsMany': '{n} huéspedes',
  'activityPhone.createdBy': 'Creado por {name}',

  // ── Teléfono: listas de Admin Users, Negocios, Contactos y Eventos a 375 px (SAM-1308) ──
  'phoneLists.adminUsers.search': 'Buscar usuarios administradores',
  'phoneLists.adminUsers.summary': '{n} usuarios administradores',
  'phoneLists.adminUsers.create': 'Crear usuario de negocio',
  'phoneLists.adminUsers.otherRole': 'Otro rol…',
  'phoneLists.adminUsers.empty': 'No hay usuarios administradores para mostrar.',
  'phoneLists.adminUsers.disabled': 'Deshabilitado',
  'phoneLists.adminUsers.status.CONFIRMED': 'Confirmado',
  'phoneLists.adminUsers.status.FORCE_CHANGE_PASSWORD': 'Falta contraseña',
  'phoneLists.adminUsers.status.UNCONFIRMED': 'Sin confirmar',
  'phoneLists.business.title': 'Negocios',
  'phoneLists.business.search': 'Buscar negocios',
  'phoneLists.business.summary': '{n} negocios',
  'phoneLists.business.filterLabel': 'Filtrar por estado',
  'phoneLists.business.all': 'Todos',
  'phoneLists.business.active': 'Activos',
  'phoneLists.business.inactive': 'Inactivos',
  'phoneLists.business.empty': 'No se encontraron negocios.',
  'phoneLists.business.loading': 'Cargando negocios…',
  'phoneLists.business.open': 'Abierto · {label}',
  'phoneLists.business.closed': 'Cerrado · {label}',
  'phoneLists.business.unnamed': 'Sin nombre',
  'phoneLists.contacts.title': 'Contactos',
  'phoneLists.contacts.search': 'Buscar contactos',
  'phoneLists.contacts.empty': 'No hay contactos disponibles.',
  'phoneLists.contacts.loading': 'Cargando contactos…',
  'phoneLists.contacts.untitled': 'Contacto',
  'phoneLists.contacts.guest': 'Huésped {id}',
  'phoneLists.contacts.from': 'Invitación desde {date}',
  'phoneLists.events.title': 'Eventos',
  'phoneLists.events.search': 'Buscar eventos',
  'phoneLists.events.filterLabel': 'Mostrar eventos',
  'phoneLists.events.all': 'Todos',
  'phoneLists.events.empty': 'No hay eventos que coincidan.',
  'phoneLists.events.none': 'Todavía no hay eventos.',
  'phoneLists.events.people': '{n} personas',
  'phoneLists.events.waiting': 'Esperando a {tenant}',
  'phoneLists.events.waitingApproval': 'Esperando aprobación',
  'phoneLists.events.untitled': 'Evento sin nombre',
};
